import { useState } from "react";
import { Star, BadgeCheck } from "lucide-react";
import { GOOGLE_REVIEWS, REVIEW_CATEGORIES } from "@/data/content";
import { truncateAtSentence } from "@/lib/reviewText";

function GoogleG({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.7 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.9 6.1C12.5 13.5 17.8 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.6-4.9 7.3l7.5 5.8c4.4-4 7.2-10 7.2-17.6z"/>
      <path fill="#FBBC05" d="M10.5 28.6c-.5-1.5-.8-3-.8-4.6s.3-3.1.8-4.6l-7.9-6.1C.9 16.4 0 20.1 0 24s.9 7.6 2.6 10.7l7.9-6.1z"/>
      <path fill="#34A853" d="M24 48c6.3 0 11.6-2.1 15.4-5.7l-7.5-5.8c-2.1 1.4-4.8 2.2-7.9 2.2-6.2 0-11.5-4-13.5-9.9l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/>
    </svg>
  );
}

function ReviewCard({ r, idx, testidPrefix }) {
  const [expanded, setExpanded] = useState(false);
  const { preview, isTruncated, endsAtSentence } = truncateAtSentence(r.text);
  return (
    <article
      data-testid={`${testidPrefix}-${idx}`}
      className="review-shadow review-shadow-hover rounded-2xl border border-[var(--maqam-border)] bg-white p-7 flex flex-col h-full"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center text-white font-serif text-base"
            style={{ background: "var(--maqam-navy)" }}
          >
            {r.initial}
          </div>
          <div>
            <p className="text-sm font-semibold text-[color:var(--maqam-navy)] flex items-center gap-1.5">
              {r.name}
              <BadgeCheck size={13} className="text-[color:var(--maqam-gold)]" />
            </p>
            <p className="text-[10px] text-[var(--maqam-muted)] uppercase tracking-wider mt-0.5">
              {[r.location, r.date].filter(Boolean).join(" · ")}
            </p>
          </div>
        </div>
        <GoogleG size={16} />
      </div>
      <div className="mt-3 flex items-center gap-0.5">
        {[0, 1, 2, 3, 4].map((s) => (
          <Star key={s} size={14} className="text-[color:var(--maqam-gold)]" style={{ fill: "currentColor" }} />
        ))}
      </div>
      <p className="mt-4 text-sm text-[var(--maqam-ink)] leading-relaxed flex-1">
        “{isTruncated && !expanded ? `${preview}${endsAtSentence ? "" : "…"}` : r.text}”
        {isTruncated && (
          <button
            type="button"
            data-testid={`${testidPrefix}-${idx}-toggle`}
            onClick={() => setExpanded((v) => !v)}
            className="ml-1.5 align-baseline inline-flex items-center text-xs font-semibold text-[color:var(--maqam-navy)] underline underline-offset-4 decoration-[color:var(--maqam-gold)] hover:decoration-2 transition-all"
            aria-expanded={expanded}
          >
            {expanded ? "Show Less" : "Read More"}
          </button>
        )}
      </p>
      <p className="mt-5 text-[10px] uppercase tracking-[0.18em] text-[color:var(--maqam-gold-2)]">
        Verified Google Review
      </p>
    </article>
  );
}

export default function CategoryReviews() {
  const [active, setActive] = useState("all");

  const counts = REVIEW_CATEGORIES.reduce((acc, c) => {
    acc[c.key] = GOOGLE_REVIEWS.filter((r) => r.category === c.key).length;
    return acc;
  }, {});

  const filtered =
    active === "all"
      ? GOOGLE_REVIEWS
      : GOOGLE_REVIEWS.filter((r) => r.category === active);

  const activeCategory = REVIEW_CATEGORIES.find((c) => c.key === active);

  return (
    <section
      data-testid="category-reviews"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="max-w-2xl">
          <span className="overline">All Reviews</span>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl text-[color:var(--maqam-navy)] leading-[1.05] tracking-tight">
            Reviews, organized by what pilgrims came for.
          </h2>
          <p className="mt-5 text-[var(--maqam-muted)] leading-relaxed">
            Every review here is verified on Google. Filter by the topic that matters most to you.
          </p>
        </div>

        {/* Category chips */}
        <div className="mt-10 flex flex-wrap gap-2">
          <button
            data-testid="category-chip-all"
            onClick={() => setActive("all")}
            className={`px-4 py-2 text-sm rounded-full border transition-colors ${
              active === "all"
                ? "bg-[color:var(--maqam-navy)] text-white border-[color:var(--maqam-navy)]"
                : "text-[color:var(--maqam-navy)] border-[var(--maqam-border)] hover:border-[color:var(--maqam-navy)]"
            }`}
          >
            All Reviews <span className="ml-1 text-xs opacity-70">({GOOGLE_REVIEWS.length})</span>
          </button>
          {REVIEW_CATEGORIES.map((c) => (
            <button
              key={c.key}
              data-testid={`category-chip-${c.key}`}
              onClick={() => setActive(c.key)}
              className={`px-4 py-2 text-sm rounded-full border transition-colors ${
                active === c.key
                  ? "bg-[color:var(--maqam-navy)] text-white border-[color:var(--maqam-navy)]"
                  : "text-[color:var(--maqam-navy)] border-[var(--maqam-border)] hover:border-[color:var(--maqam-navy)]"
              }`}
            >
              {c.label} <span className="ml-1 text-xs opacity-70">({counts[c.key] || 0})</span>
            </button>
          ))}
        </div>

        {activeCategory && (
          <p className="mt-6 text-sm text-[var(--maqam-muted)] italic">{activeCategory.blurb}</p>
        )}

        {/* Grid */}
        <div className="mt-10">
          {filtered.length === 0 ? (
            <div
              data-testid="category-empty-state"
              className="review-shadow rounded-3xl border border-dashed border-[var(--maqam-border)] bg-[var(--maqam-surface)] p-10 md:p-14 text-center"
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-[var(--maqam-border)] bg-white px-4 py-2 mb-5">
                <BadgeCheck size={14} className="text-[color:var(--maqam-gold)]" />
                <span className="text-[11px] tracking-[0.18em] uppercase text-[color:var(--maqam-navy)] font-semibold">
                  Verified reviews only
                </span>
              </div>
              <h3 className="font-serif text-2xl text-[color:var(--maqam-navy)] leading-snug max-w-xl mx-auto">
                No reviews yet in this category.
              </h3>
              <p className="mt-3 text-[var(--maqam-muted)] max-w-lg mx-auto">
                We only display authentic reviews from real pilgrims. More stories will appear here as our community grows.
              </p>
              <button
                type="button"
                onClick={() => setActive("all")}
                data-testid="category-show-all"
                className="mt-6 inline-flex items-center gap-2 btn-gold rounded-full px-6 py-3 font-semibold"
              >
                Show all reviews
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((r, i) => (
                <ReviewCard key={`${r.name}-${i}`} r={r} idx={i} testidPrefix={`review-${active}`} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
